import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface DeliveryNotificationItem {
    id: number;
    name: string;
    email?: string; // Kept for legacy compatibility if needed
    emails?: EmailItem[];
    isActive: boolean;
    countryId?: number;
    countryName?: string;
    createdDateTime?: Date;
    updatedDateTime?: Date;
}

export interface EmailItem {
    id?: number;
    email: string;
    isActive: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class DeliveryNotificationService {
    private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/delivery-notification`;

    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    /**
     * Get all delivery notifications with pagination
     */
    public getAll(
        pageIndex?: number,
        pageSize?: number,
        searchValue?: string,
        countryId?: number | number[]
    ): Observable<{ data: DeliveryNotificationItem[]; paginatorValue: { totalData: number; totalCount: number } }> {
        let params = new HttpParams();
        if (pageIndex !== undefined && pageIndex !== null) {
            params = params.set('pageIndex', pageIndex.toString());
        }
        if (pageSize !== undefined && pageSize !== null) {
            params = params.set('pageSize', pageSize.toString());
        }
        if (searchValue && searchValue.trim() !== '') {
            params = params.set('searchValue', searchValue.trim());
        }
        if (countryId !== undefined && countryId !== null) {
            if (Array.isArray(countryId)) {
                if (countryId.length > 0) {
                    params = params.set('countryId', countryId.join(','));
                }
            } else {
                params = params.set('countryId', countryId.toString());
            }
        }
        return this.http
            .get<{ data: DeliveryNotificationItem[]; paginatorValue: { totalData: number; totalCount: number } }>(`${this.URL_PATH}/getAll`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Create a new delivery notification
     */
    public create(data: Partial<DeliveryNotificationItem>): Observable<{ message: string; result: DeliveryNotificationItem }> {
        return this.http
            .post<{ message: string; result: DeliveryNotificationItem }>(`${this.URL_PATH}/create`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Update an existing delivery notification
     */
    public update(id: number, data: Partial<DeliveryNotificationItem>): Observable<{ message: string; result: DeliveryNotificationItem }> {
        return this.http
            .put<{ message: string; result: DeliveryNotificationItem }>(`${this.URL_PATH}/update/${id}`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Delete a delivery notification
     */
    public delete(id: number): Observable<{ message: string }> {
        return this.http
            .delete<{ message: string }>(`${this.URL_PATH}/delete/${id}`)
            .pipe(catchError(this.errorHandler.handleError));
    }
}
